import { startLogin, type CognitoConfig, type TokenSet } from './cognito';

/*
 * Alta con correo y contraseña dentro de la app (15 oct 2026).
 *
 * «Crear la cuenta» después del chat de la landing: resumen de lo contado y,
 * en la misma pantalla, correo y contraseña, sin pasar por el formulario de
 * Cognito. Se habla con la API pública de Cognito (sin credenciales de AWS;
 * solo el client id de la SPA): SignUp → código por correo → ConfirmSignUp →
 * InitiateAuth USER_PASSWORD_AUTH (activado en infra/lib/auth.ts). Si ese
 * flujo aún no está activado en el entorno, la app cae al managed login con
 * el correo ya puesto: la cuenta existe y está confirmada, solo falta entrar.
 *
 * «Continuar con Google» queda fuera por ahora (la app móvil necesitaría
 * «Iniciar sesión con Apple»); el hueco está en AltaCuenta.
 */

export type AltaErrorCode = 'existe' | 'ya-confirmada' | 'contrasena' | 'codigo' | 'codigo-caducado' | 'muchos-intentos' | 'sin-flujo' | 'otro';

export class AltaError extends Error {
  constructor(
    message: string,
    readonly code: AltaErrorCode,
  ) {
    super(message);
  }
}

/** La política del pool (infra/lib/auth.ts): 10 caracteres, mayúscula, minúscula y número. */
export function contrasenaValida(password: string): boolean {
  return password.length >= 10 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password);
}

/** Región del pool: la del dominio de Cognito, la de la variable o Irlanda. */
export function cognitoRegion(config: Pick<CognitoConfig, 'domain'>): string {
  const delDominio = /\.auth\.([a-z0-9-]+)\.amazoncognito\.com/.exec(config.domain)?.[1];
  return delDominio ?? ((import.meta.env.VITE_COGNITO_REGION ?? '').trim() || 'eu-west-1');
}

/** Lo que se dice cuando Cognito responde algo que no esperábamos: nunca su texto en inglés. */
const ERROR_GENERICO = 'Algo no fue bien al crear tu cuenta. Vuelve a probar en un momento.';

/**
 * Traduce el error de Cognito a una frase para la persona. Exportada para los tests.
 * Ningún mensaje de Cognito llega tal cual a la pantalla: van en inglés (bugbot 15 oct 2026).
 */
export function errorDeCognito(tipo: string, mensaje: string): AltaError {
  // ConfirmSignUp otra vez con la cuenta ya confirmada (el código entró, pero
  // falló el paso de entrar): no es un error, toca entrar.
  if ((tipo === 'NotAuthorizedException' && /CONFIRMED/.test(mensaje)) || (tipo === 'InvalidParameterException' && /already confirmed/i.test(mensaje))) {
    return new AltaError('Tu correo ya está confirmado. Inicia sesión con él.', 'ya-confirmada');
  }
  switch (tipo) {
    case 'UsernameExistsException':
      return new AltaError('Ya hay una cuenta con este correo. Inicia sesión con él.', 'existe');
    case 'InvalidPasswordException':
      return new AltaError('La contraseña necesita al menos 10 caracteres, con mayúsculas, minúsculas y algún número.', 'contrasena');
    case 'CodeMismatchException':
      return new AltaError('Ese código no es el que te mandamos. Revísalo y vuelve a escribirlo.', 'codigo');
    case 'ExpiredCodeException':
      return new AltaError('El código caducó. Pide uno nuevo.', 'codigo-caducado');
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return new AltaError('Demasiados intentos seguidos. Espera unos minutos y vuelve a probar.', 'muchos-intentos');
    case 'NotAuthorizedException':
      return new AltaError('El correo o la contraseña no coinciden.', 'otro');
    case 'UserNotFoundException':
      return new AltaError('No encontramos una cuenta con este correo. Vuelve a crearla.', 'otro');
    case 'CodeDeliveryFailureException':
      return new AltaError('No pudimos mandarte el código. Revisa el correo y vuelve a probar.', 'otro');
    default:
      // `sin-flujo` no se enseña tal cual: AltaCuenta pone su propia frase (y cae al login de Cognito).
      if (/USER_PASSWORD_AUTH|not enabled|flow/i.test(mensaje)) return new AltaError(mensaje, 'sin-flujo');
      if (tipo === 'InvalidParameterException') return new AltaError('Revisa el correo: no parece válido.', 'otro');
      return new AltaError(ERROR_GENERICO, 'otro');
  }
}

async function cognitoIdp<T>(config: CognitoConfig, accion: string, cuerpo: Record<string, unknown>): Promise<T> {
  const response = await fetch(`https://cognito-idp.${cognitoRegion(config)}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-amz-json-1.1',
      'x-amz-target': `AWSCognitoIdentityProviderService.${accion}`,
    },
    body: JSON.stringify({ ClientId: config.clientId, ...cuerpo }),
  });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (response.ok) return data as T;
  const tipo = String(data.__type ?? '').split('#').pop() ?? '';
  throw errorDeCognito(tipo, String(data.message ?? data.Message ?? ''));
}

/** Registra la cuenta. Cognito manda un código al correo para confirmarla. */
export async function registrarConCorreo(
  config: CognitoConfig,
  datos: { email: string; password: string; nombre?: string },
): Promise<{ confirmada: boolean }> {
  const atributos = [{ Name: 'email', Value: datos.email }];
  if (datos.nombre) atributos.push({ Name: 'name', Value: datos.nombre });
  const res = await cognitoIdp<{ UserConfirmed?: boolean }>(config, 'SignUp', {
    Username: datos.email,
    Password: datos.password,
    UserAttributes: atributos,
  });
  return { confirmada: res.UserConfirmed === true };
}

export async function confirmarCodigo(config: CognitoConfig, email: string, codigo: string): Promise<void> {
  await cognitoIdp(config, 'ConfirmSignUp', { Username: email, ConfirmationCode: codigo.replace(/\s+/g, '') });
}

export async function reenviarCodigo(config: CognitoConfig, email: string): Promise<void> {
  await cognitoIdp(config, 'ResendConfirmationCode', { Username: email });
}

/** Entra con correo y contraseña. Devuelve los mismos tokens que el managed login. */
export async function entrarConCorreo(config: CognitoConfig, email: string, password: string): Promise<TokenSet> {
  const res = await cognitoIdp<{ AuthenticationResult?: { IdToken?: string; RefreshToken?: string; ExpiresIn?: number }; ChallengeName?: string }>(
    config,
    'InitiateAuth',
    { AuthFlow: 'USER_PASSWORD_AUTH', AuthParameters: { USERNAME: email, PASSWORD: password } },
  );
  const r = res.AuthenticationResult;
  // Un reto (MFA) no se resuelve aquí: que entre por el managed login.
  if (!r?.IdToken) throw new AltaError(res.ChallengeName ?? 'Sin tokens', 'sin-flujo');
  return { idToken: r.IdToken, refreshToken: r.RefreshToken, expiresIn: r.ExpiresIn };
}

/** Managed login con el correo ya escrito (la reserva si no se puede entrar aquí). */
export function entrarPorCognito(config: CognitoConfig, email: string): Promise<void> {
  return startLogin(config, undefined, { loginHint: email });
}
